"use client";

import { useGetAuthorsQuery } from "@/api/AppApi/services/blogs";
import { IBlogAuthorListItem } from "@/api/AppApi/services/blogs/interfaces/IBlogs";
import BoxWithTitle from "../BoxWithTitle";
import { useSearchParams } from "next/navigation";
import { useBlogPushQuery } from "@/utils/functions/useBlogPushQuery";
import { useTranslation } from "@/hooks/localization/useTranslations";
import SvgSearchIcon from "@/app/layouts/header/header-common/search/search-helpers/search-icons/SvgSearchIcon";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { numAsString } from "@/utils/functions/functions";
import useDebounce from "@/hooks/useDebounce";
import GeneralApiLoader from "@/app/components/lib/loaders/GeneralApiLoader";

interface Props {
  title: string;
}

const AuthorsFilter: React.FC<Props> = ({ title }) => {
  const searchParams = useSearchParams();
  const { pushQueryToRouter } = useBlogPushQuery();
  const t = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useGetAuthorsQuery({
    params: { name: debouncedSearchTerm || undefined, per_page: 50 },
  });

  const isShowLoading = isLoading || isFetching;

  const authors: IBlogAuthorListItem[] = data?.result?.data ?? [];

  const selectedSlugs = useMemo(() => {
    const authorsParam = searchParams?.get("authors") ?? "";
    return authorsParam ? authorsParam.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const toggleAuthor = useCallback(
    (slug: string) => {
      const newSlugs = selectedSlugs.includes(slug)
        ? selectedSlugs.filter((s) => s !== slug)
        : [...selectedSlugs, slug];

      pushQueryToRouter(
        [
          {
            name: "authors",
            val: newSlugs.length > 0 ? newSlugs.join(",") : "",
          },
        ],
        true,
      );
    },
    [selectedSlugs, pushQueryToRouter],
  );

  const visibleAuthors = useMemo(() => {
    if (showAll) return authors;
    
    const selectedAuthors = authors.filter(a => selectedSlugs.includes(a.slug));
    const otherAuthors = authors.filter(a => !selectedSlugs.includes(a.slug));
    
    const result = [...selectedAuthors];
    if (result.length < 5) {
      result.push(...otherAuthors.slice(0, 5 - result.length));
    }
    return result;
  }, [authors, selectedSlugs, showAll]);

  const hasMore = authors.length > visibleAuthors.length;

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <BoxWithTitle
      title={title}
      endLabel={{
        label: (
          <SvgSearchIcon
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="fill-primary"
          />
        ),
        onClick: () => setIsSearchOpen(!isSearchOpen),
      }}
    >
      {isSearchOpen && (
        <input
          ref={inputRef}
          type="text"
          className="author-search-input"
          placeholder={t.search_authors}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}
      {isShowLoading ? (
        <div className="authors-loading" style={{ paddingTop: 8 }}>
          <GeneralApiLoader />
        </div>
      ) : authors.length === 0 ? (
        <div
          className="empty-authors text-center py-4"
          style={{ color: "var(--v2-gray4)" }}
        >
          <p className="m-0" style={{ fontSize: 14 }}>
            {t.no_authors}
          </p>
        </div>
      ) : (
        <ul className="m-0 p-0" style={{ listStyleType: "none" }}>
          {visibleAuthors.map((author) => {
            const isActive = selectedSlugs.includes(author.slug);
            return (
              <li key={author.slug}>
                <div
                  className={`blog-filter author-item ${isActive ? "active" : ""}`}
                  onClick={() => toggleAuthor(author.slug)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      toggleAuthor(author.slug);
                    }
                  }}
                >
                  <p>
                    {isActive && <span style={{ marginInlineEnd: 6 }}>✕</span>}
                    {author.name}
                  </p>
                  <p className="arbaic-num">{`(${numAsString(author.blogs_count ?? 0)})`}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {hasMore && !isShowLoading && (
        <button className="show-more-btn" onClick={() => setShowAll(!showAll)}>
          {showAll ? t.show_less : t.show_more}
        </button>
      )}
    </BoxWithTitle>
  );
};

export default AuthorsFilter;
